import * as React from 'react'
import { useHistory } from 'react-router-dom'
import { colors, Flex, StyledText, TextField, Touchable, useTheme } from '@dooer/react-ui-kit'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faChevronLeft, faSearch } from '@fortawesome/pro-light-svg-icons'
import styles from '../breadcrumb/breadcrumb.module.scss'
import AcademyCategoryIcon from '../../academy-category-icon'
import isMobile from 'ismobilejs'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { defineMessages, useIntl } from 'react-intl'

const messages = defineMessages({
  academySearch: {
    defaultMessage: 'Sökresultat för "{academySearch}"',
    id: 'academy.breadcrumb.academy-search',
  },
})

const Breadcrumb = (props: {
  academySearch?: string | null
  title: string
  path: string
  last: boolean
  first: boolean
}) => {
  const history = useHistory()
  const theme = useTheme()

  return (
    <Touchable
      onClick={() =>
        props.path === 'hjalp'
          ? history.push(`/hjalp`)
          : history.push({
              pathname: `/hjalp/${props.path}`,
              search: props.academySearch ? `academySearch=${props.academySearch}` : '',
            })
      }
    >
      <StyledText
        size="medium"
        style={{
          color: props.last ? theme.colors.text.normal : theme.colors.ui.light,
          fontFamily: 'aktiv-grotesk',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontSize: 14,
          ':hover': {
            color: colors.accents.primaryHover,
            textDecoration: 'underline',
          },
          ...(props.first ? { flexShrink: 0 } : {}),
        }}
        weight={400}
      >
        {props.title}
      </StyledText>
    </Touchable>
  )
}

const Delimiter = (icon: IconProp) => (last: boolean) => {
  return (
    <StyledText
      size="small"
      style={{
        marginLeft: '1ch',
        marginRight: '1ch',
        userSelect: 'none',
      }}
      variant={last ? 'normal' : 'dim'}
    >
      <FontAwesomeIcon icon={icon} size="sm" />
    </StyledText>
  )
}

interface IBreadcrumbsProps {
  academySearch?: string | null
  delimiter: (last: boolean) => React.ReactNode
  pathTuples: Array<{ path: string; title: string }>
}

const Breadcrumbs = (props: IBreadcrumbsProps) => {
  const history = useHistory()
  const intl = useIntl()

  return (
    <Flex alignItems="center" height={18} justifyContent="flex-start" row style={{ whiteSpace: 'nowrap' }} width="100%">
      <Touchable onClick={() => history.push(`/hjalp`)}>
        <AcademyCategoryIcon category={{ icon: 'academy' }} iconStyle="header" />
      </Touchable>
      {props.academySearch && (
        <>
          {props.delimiter(false)}
          <Breadcrumb
            academySearch={props.academySearch}
            first={false}
            last={false}
            path={''}
            title={intl.formatMessage(messages.academySearch, { academySearch: props.academySearch })}
          />
        </>
      )}
      {props.pathTuples.length !== 0 && props.delimiter(false)}
      {props.pathTuples.map((pathTuple, i) => {
        const first = i === 0
        const last = i === props.pathTuples.length - 1
        return (
          <React.Fragment key={pathTuple.path}>
            {!first && props.delimiter(last)}
            <Breadcrumb
              academySearch={props.academySearch}
              first={first}
              last={last}
              path={pathTuple.path}
              title={pathTuple.title}
            />
          </React.Fragment>
        )
      })}
    </Flex>
  )
}

interface IAcademyBreadcrumbProps {
  pathTuples: Array<{ path: string; title: string }>
  onSearchChange: (string: string) => void
  search: string | null
  onSubmit: () => void
  academySearch?: string | null
}

const AcademyBreadcrumb = (props: IAcademyBreadcrumbProps) => {
  const [, previousPath] = [...props.pathTuples].reverse()

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        {isMobile().any ? (
          <>
            <Flex alignItems="center" height={40} justifyContent="flex-start" marginLeft="large" row>
              <Breadcrumbs
                academySearch={props.academySearch}
                delimiter={Delimiter(faChevronLeft)}
                pathTuples={[previousPath || { path: '', title: 'Hjälp' }]}
              />
            </Flex>
            <Flex
              alignItems="center"
              flexGrow={1}
              height={60}
              justifyContent="center"
              marginBottom="large"
              marginLeft="large"
              marginRight="large"
              row
            >
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  props.onSubmit()
                }}
                style={{ width: '100%' }}
              >
                <TextField
                  flush
                  icon={<FontAwesomeIcon icon={faSearch} />}
                  onChange={(e) => props.onSearchChange(e.target.value)}
                  style={{ fontSize: 12, borderRadius: 34 }}
                  value={props.search || ''}
                />
              </form>
            </Flex>
          </>
        ) : (
          <Flex alignItems="center" height={60} justifyContent="center" marginLeft="large" marginRight="large" row>
            <Breadcrumbs
              academySearch={props.academySearch}
              delimiter={Delimiter(faChevronRight)}
              pathTuples={props.pathTuples}
            />
            <form
              onSubmit={(e) => {
                e.preventDefault()
                props.onSubmit()
              }}
            >
              <TextField
                flush
                icon={<FontAwesomeIcon icon={faSearch} />}
                onChange={(e) => props.onSearchChange(e.target.value)}
                style={{ fontSize: 12, borderRadius: 34 }}
                value={props.search || ''}
              />
            </form>
          </Flex>
        )}
      </div>
    </div>
  )
}

export default AcademyBreadcrumb
